import type { CSSProperties } from 'react';

export type ImageDimensions = {
  width?: number;
  height?: number;
};

const MIN_WIDTH_FOR_RESERVATION = 200;
const IMAGE_WIDTH_CSS_VAR = '--image-width';

export const hasKnownDimensions = (dimensions: ImageDimensions): boolean => {
  const { width, height } = dimensions;
  return Boolean(width && height && width > 0 && height > 0);
};

const isLargeImage = (dimensions: ImageDimensions): boolean => {
  return (
    hasKnownDimensions(dimensions) &&
    (dimensions.width ?? 0) > MIN_WIDTH_FOR_RESERVATION
  );
};

/**
 * Возвращает inline-стили для контейнера изображения.
 * Устанавливает aspect-ratio для резервирования места до загрузки.
 */
export const getImageContainerStyle = (
  dimensions: ImageDimensions
): CSSProperties | undefined => {
  const { width, height } = dimensions;

  if (!hasKnownDimensions(dimensions)) {
    return undefined;
  }

  const style: CSSProperties = {
    aspectRatio: `${width} / ${height}`
  };

  if (isLargeImage(dimensions)) {
    style.width = `min(${width}px, 100%)`;
  }

  return style;
};

/**
 * Возвращает CSS-переменную с максимальной шириной изображений для установки min-width контента сообщения.
 */
export const getContentStyleWithImageWidth = (
  images: ImageDimensions[]
): CSSProperties | undefined => {
  if (images.length === 0) {
    return undefined;
  }

  const maxWidth = Math.max(...images.map((img) => img.width ?? 0));

  if (maxWidth <= MIN_WIDTH_FOR_RESERVATION) {
    return undefined;
  }

  return {
    [IMAGE_WIDTH_CSS_VAR]: `${maxWidth}px`
  } as CSSProperties;
};
